import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FileText, ExternalLink, Download } from "lucide-react";
import { api } from "../../../../utils/api";

function UploadedDocumentsDisplay({ 
  uploadedDocuments = [], 
  applicationId = null 
}) {
  const { id: routeId } = useParams();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const effectiveAppId = applicationId || routeId;

  useEffect(() => {
    // ✅ Always fetch if we have an applicationId, regardless of uploadedDocuments
    if (effectiveAppId) {
      console.log("✅ Fetching application documents from API...");
      fetchApplication();
    } else {
      console.log("❌ No effectiveAppId available");
    }
  }, [effectiveAppId]);

  const fetchApplication = async () => {
    
    setLoading(true);
    setError(null);
    
    try {
      
      const res = await api.getApplication(effectiveAppId);

      
      // ✅ Extract data properly
      const appData = res.data?.data || res.data;
      setApplication(appData);
    } catch (e) {
      console.error("❌ API Error:", e);
      console.error("Error response:", e.response);
      
      if (e.response?.status !== 404) {
        setError("Failed to load documents");
      }
    } finally {
      setLoading(false);
    }
  };

  const documents = useMemo(() => {
   
    
    // Priority 1: Use documents from API if available (viewing existing application)
    if (application?.documents && Array.isArray(application.documents) && application.documents.length > 0) {
      
      return application.documents.map((doc) => ({
        id: doc.id,
        name: doc.file_name,
        documentType: doc.document_type_name || "Unknown",
        url: doc.preview_url || doc.file_url,
        status: "uploaded",
        isTemp: false,
      }));
    }

    // Priority 2: Use documents from props (during creation flow or if API didn't return any)
    if (uploadedDocuments && uploadedDocuments.length > 0) {
      
      return uploadedDocuments.map((doc, index) => ({
        id: doc.id || `temp-${index}`,
        name: doc.name || doc.file?.name || doc.file_name,
        documentType: doc.documentType || doc.document_type_name || "Unknown",
        url: doc.url || doc.preview_url || doc.file_url || (doc.file ? URL.createObjectURL(doc.file) : null),
        status: doc.status || "uploaded",
        isTemp: !effectiveAppId,
      }));
    }

    console.log("📄 No documents available");
    return [];
  }, [uploadedDocuments, application, effectiveAppId]);

 
  if (loading) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-3"></div>
        <p className="text-gray-600">Loading documents...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
        <p className="text-red-600 font-medium">{error}</p>
        <button
          onClick={fetchApplication}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">No documents uploaded yet</p>
        <p className="text-sm text-gray-500 mt-1">
          Documents will appear here after you upload them
        </p>
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-left text-xs">
          <strong>Debug Info:</strong><br/>
          applicationId (prop): {applicationId || "null"}<br/>
          routeId (param): {routeId || "null"}<br/>
          effectiveAppId: {effectiveAppId || "null"}<br/>
          uploadedDocuments length: {uploadedDocuments?.length || 0}<br/>
          application loaded: {application ? "yes" : "no"}<br/>
          application.documents: {application?.documents?.length || 0}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Documents
        </h3>
        <span className="text-sm text-gray-600">
          {documents.length} {documents.length === 1 ? "document" : "documents"}
        </span>
      </div>

      {documents.some(doc => doc.isTemp) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            📄 Documents are ready for processing. They will be permanently saved after form submission.
          </p>
        </div>
      )}

      <div className="grid gap-3">
        {documents.map((doc) => (
          <DocumentCard key={doc.id} document={doc} />
        ))}
      </div>
    </div>
  );
}

// Document Card Component
function DocumentCard({ document }) {
  const handleView = () => {
    if (document.url) {
      window.open(document.url, "_blank");
    }
  };

  const handleDownload = async () => {
    if (!document.url) return;

    try {
      const response = await fetch(document.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = document.name;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-blue-600" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-gray-900 truncate">
              {document.documentType}
              
            </h4>
            <p className="text-sm text-gray-600">
             {document.name}
            </p>
            {document.isTemp && (
              <p className="text-xs text-amber-600 mt-1">
                ⏳ Pending save
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {document.url && (
            <>
              <button
                onClick={handleView}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="View document"
              >
                <ExternalLink className="w-4 h-4 text-gray-600" />
              </button>
              <button
                onClick={handleDownload}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Download document"
              >
                <Download className="w-4 h-4 text-gray-600" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default UploadedDocumentsDisplay;